<template>
  <div class="coupon-stats-container">
    <!-- 头部 -->
    <el-card class="header-card">
      <div class="header-content">
        <div class="header-left">
          <h2>卡券销售统计</h2>
        </div>
        <div class="header-right">
          <el-input
              v-model="searchQuery.keyword"
              placeholder="搜索卡券名称"
              prefix-icon="el-icon-search"
              style="width: 200px; margin-right: 10px;"
              @keyup.enter.native="handleSearch"
              clearable
              @clear="handleSearch"
              size="small"
          />
          <el-button @click="refreshData" size="small">
            <i class="el-icon-refresh"></i>
            刷新
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 卡券统计表格 -->
    <el-card class="table-card">
      <div v-loading="loading">
        <el-table
            :data="statsList"
            style="width: 100%"
            :header-cell-style="{background: '#f5f7fa', color: '#606266'}"
        >
          <el-table-column prop="id" label="ID" width="70"/>
          <el-table-column prop="name" label="卡券名称" min-width="160">
            <template slot-scope="{row}">
              <span class="coupon-name">{{ row.name }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="price" label="单价(元)" width="100" align="right">
            <template slot-scope="{row}">
              {{ row.price ? (row.price / 100).toFixed(2) : '--' }}
            </template>
          </el-table-column>
          <el-table-column prop="totalCount" label="总销量" width="90" align="center">
            <template slot-scope="{row}">
              <el-tag type="info" size="small">{{ row.totalCount || 0 }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="usedCount" label="已核销" width="90" align="center">
            <template slot-scope="{row}">
              <el-tag type="success" size="small">{{ row.usedCount || 0 }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="unusedCount" label="未核销" width="90" align="center">
            <template slot-scope="{row}">
              <el-tag type="warning" size="small">{{ row.unusedCount || 0 }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="expiredCount" label="已过期" width="80" align="center">
            <template slot-scope="{row}">
              <span class="expired-count">{{ row.expiredCount || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="refundedCount" label="已退款" width="80" align="center">
            <template slot-scope="{row}">
              <span class="refunded-count">{{ row.refundedCount || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="totalAmount" label="销售总额(元)" width="120" align="right">
            <template slot-scope="{row}">
              <span class="amount">{{ row.totalAmount ? (row.totalAmount / 100).toFixed(2) : '0.00' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="{row}">
              <el-button type="text" size="small" @click="handleViewDetail(row)">
                <i class="el-icon-view"></i>
                明细
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
              :current-page="pagination.currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              :total="pagination.total"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 卡券明细弹窗 -->
    <el-dialog
        :title="`卡券明细 - ${currentCoupon.name || ''}`"
        :visible.sync="detailDialogVisible"
        width="800px"
        :close-on-click-modal="false"
        append-to-body
    >
      <div class="detail-dialog-content">
        <!-- 筛选 -->
        <div class="detail-filter">
          <el-select
              v-model="detailQuery.status"
              placeholder="券状态"
              style="width: 120px; margin-right: 10px;"
              clearable
              @change="loadVoucherDetail"
              size="small"
          >
            <el-option label="全部状态" :value="null"/>
            <el-option label="待使用" :value="0"/>
            <el-option label="已使用" :value="1"/>
            <el-option label="已过期" :value="2"/>
            <el-option label="已退款" :value="3"/>
          </el-select>
          <el-input
              v-model="detailQuery.keyword"
              placeholder="搜索券码/手机号"
              style="width: 200px;"
              clearable
              @keyup.enter.native="loadVoucherDetail"
              @clear="loadVoucherDetail"
              size="small"
          />
        </div>

        <!-- 明细表格 -->
        <el-table
            :data="voucherDetailList"
            style="width: 100%; margin-top: 10px;"
            :header-cell-style="{background: '#f5f7fa', color: '#606266'}"
            v-loading="detailLoading"
        >
          <el-table-column prop="voucherCode" label="券码" min-width="160">
            <template slot-scope="{row}">
              <span class="voucher-code">{{ row.voucherCode }}</span>
            </template>
          </el-table-column>
          <el-table-column label="用户" min-width="140">
            <template slot-scope="{row}">
              <div v-if="row.userPhone">
                <div>{{ row.userPhone }}</div>
                <div class="user-nickname" v-if="row.userNickname">{{ row.userNickname }}</div>
              </div>
              <span v-else>--</span>
            </template>
          </el-table-column>
          <el-table-column prop="purchasePrice" label="购买金额(元)" width="110" align="right">
            <template slot-scope="{row}">
              {{ row.purchasePrice ? (row.purchasePrice / 100).toFixed(2) : '--' }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="90">
            <template slot-scope="{row}">
              <el-tag :type="getVoucherStatusType(row.status)" size="small">
                {{ getVoucherStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="expireTime" label="过期时间" width="160">
            <template slot-scope="{row}">
              {{ row.expireTime ? formatTime(row.expireTime) : '--' }}
            </template>
          </el-table-column>
          <el-table-column prop="useTime" label="核销时间" width="160">
            <template slot-scope="{row}">
              {{ row.useTime ? formatTime(row.useTime) : '--' }}
            </template>
          </el-table-column>
        </el-table>

        <!-- 明细分页 -->
        <div class="detail-pagination">
          <el-pagination
              :current-page="detailPagination.currentPage"
              :page-sizes="[10, 20, 50]"
              :page-size="detailPagination.pageSize"
              :total="detailPagination.total"
              layout="total, sizes, prev, pager, next"
              @size-change="handleDetailSizeChange"
              @current-change="handleDetailCurrentChange"
          />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from "@/utils/api"

export default {
  name: 'CouponStats',
  data() {
    return {
      loading: false,
      searchQuery: {
        keyword: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      statsList: [],

      detailDialogVisible: false,
      detailLoading: false,
      currentCoupon: {},
      detailQuery: {
        status: null,
        keyword: ''
      },
      detailPagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      voucherDetailList: []
    }
  },

  mounted() {
    this.loadStatsList()
  },

  methods: {
    async loadStatsList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
          keyword: this.searchQuery.keyword || undefined
        }
        const res = await api.coupon_getCouponStatsList(params, null)
        if (res.data.code === '000') {
          const data = res.data.data
          this.statsList = data.list || data.records || data || []
          this.pagination.total = data.totalCount || data.total || this.statsList.length
        }
      } catch (e) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },

    async loadVoucherDetail() {
      this.detailLoading = true
      try {
        const params = {
          productId: this.currentCoupon.id,
          page: this.detailPagination.currentPage,
          pageSize: this.detailPagination.pageSize,
          status: this.detailQuery.status !== null ? this.detailQuery.status : undefined,
          keyword: this.detailQuery.keyword || undefined
        }
        const res = await api.coupon_getVoucherDetailList(params, null)
        if (res.data.code === '000') {
          const data = res.data.data
          this.voucherDetailList = data.list || data.records || data || []
          this.detailPagination.total = data.totalCount || data.total || this.voucherDetailList.length
        }
      } catch (e) {
        this.$message.error('加载失败')
      } finally {
        this.detailLoading = false
      }
    },

    handleViewDetail(row) {
      this.currentCoupon = row
      this.detailQuery = { status: null, keyword: '' }
      this.detailPagination = { currentPage: 1, pageSize: 10, total: 0 }
      this.detailDialogVisible = true
      this.loadVoucherDetail()
    },

    formatTime(timestamp) {
      if (!timestamp) return '--'
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },

    getVoucherStatusText(status) {
      const map = { 0: '待使用', 1: '已使用', 2: '已过期', 3: '已退款' }
      return map[status] || '未知'
    },

    getVoucherStatusType(status) {
      const map = { 0: 'warning', 1: 'success', 2: 'info', 3: 'danger' }
      return map[status] || 'info'
    },

    handleSearch() {
      this.pagination.currentPage = 1
      this.loadStatsList()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1
      this.loadStatsList()
    },

    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.loadStatsList()
    },

    handleDetailSizeChange(size) {
      this.detailPagination.pageSize = size
      this.detailPagination.currentPage = 1
      this.loadVoucherDetail()
    },

    handleDetailCurrentChange(page) {
      this.detailPagination.currentPage = page
      this.loadVoucherDetail()
    },

    refreshData() {
      this.searchQuery = { keyword: '' }
      this.pagination.currentPage = 1
      this.loadStatsList()
    }
  }
}
</script>

<style scoped lang="scss">
.coupon-stats-container { padding: 20px; }

.header-card {
  margin-bottom: 20px;
  .header-content { display: flex; justify-content: space-between; align-items: center; }
  .header-left h2 { margin: 0; font-size: 24px; color: #303133; }
  .header-right { display: flex; align-items: center; gap: 10px; }
}

.table-card {
  .coupon-name { font-weight: 500; }
  .amount { font-weight: 600; color: #f56c6c; }
  .expired-count { color: #909399; }
  .refunded-count { color: #f56c6c; }
}

.pagination-container { display: flex; justify-content: flex-end; margin-top: 20px; padding-top: 20px; border-top: 1px solid #ebeef5; }

.detail-dialog-content {
  .detail-filter { display: flex; align-items: center; }
  .voucher-code { font-family: monospace; color: #409eff; }
  .user-nickname { font-size: 12px; color: #909399; }
  .detail-pagination { display: flex; justify-content: flex-end; margin-top: 15px; }
}
</style>